import type { InternalAxiosRequestConfig, AxiosError } from 'axios';

import router from '@/app/providers/router';
import {
  isBasicAuthChallenge,
  isLocationRequiredError,
  isOrganizationNotFoundError,
  reloadForBasicAuthChallenge,
  reloadForLocationSelection,
  reloadWithoutStaleTenant,
  useAuthStore,
} from '@/entities/auth';
import { handleSessionExpired } from '@/features/session';
import { instance } from '@/shared/api';
import { useLocationStorage, useOrganizationStorage } from '@/shared/config';

/**
 * Event для глобальной обработки ошибок авторизации
 * Можно подписаться на это событие, чтобы обработать 401/403
 */
export const AUTH_ERROR_EVENT = 'http:auth-error';

/**
 * Настройка HTTP интерсепторов для добавления X-Location-Id или X-Organization-Id и обработки ошибок
 * Вызывается при инициализации приложения после создания Pinia
 */
export function setupHttpInterceptors(): void {
  /**
   * Request Interceptor: добавляем X-Location-Id если есть, иначе X-Organization-Id (fallback)
   */
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const authStore = useAuthStore();

    // Тот же порядок, что у Apollo: локация вкладки, затем стор.
    const locationId = useLocationStorage().getLocationId() || authStore.currentLocation?.id;

    // Приоритет 1: Если есть location - отправляем только её
    if (locationId) {
      config.headers.set('X-Location-Id', locationId);
    } else {
      // Приоритет 2 (fallback): Если нет location - отправляем organization
      const organizationId = useOrganizationStorage().getOrganizationId() || authStore.currentOrganization?.id;

      if (organizationId) {
        config.headers.set('X-Organization-Id', organizationId);
      }
    }

    return config;
  });

  /**
   * Response Interceptor: обработка ошибок авторизации, NOT_EMPLOYEE и LOCATION_HEADER_REQUIRED
   */
  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError<{ error_code?: string }>) => {
      const status = error.response?.status;

      if (isBasicAuthChallenge(error)) {
        reloadForBasicAuthChallenge(error.config?.url);

        return Promise.reject(error);
      }

      if (status === 401 || status === 419) {
        if (status === 401) {
          // eslint-disable-next-line no-console -- Диагностика fallback доступна в браузере на уровне Verbose.
          console.debug('[auth] 401 без WWW-Authenticate, трактуем как истёкшую сессию');
        }

        handleSessionExpired(router);
      }

      // Специальная обработка 403 NOT_EMPLOYEE
      if (status === 403 && error.response?.data?.error_code === 'NOT_EMPLOYEE') {
        useAuthStore().clearContext();
        reloadForLocationSelection();
      }

      if (isLocationRequiredError(error)) {
        reloadForLocationSelection();
      }

      if (isOrganizationNotFoundError(error)) {
        reloadWithoutStaleTenant();
      }

      // Для остальных ошибок авторизации отправляем событие
      if (status && [401, 419, 403].includes(status)) {
        window.dispatchEvent(
          new CustomEvent(AUTH_ERROR_EVENT, {
            detail: { status },
          }),
        );
      }

      return Promise.reject(error);
    },
  );
}
